import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { useTheme } from 'styled-components';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';

import Svg from '@svg/index';
import { Flex } from '@ui-library/flex';
import { Tooltip } from '@ui-library/tooltip';
import { InputField } from '@ui-library/input';
import Backdrop from '@ui-library/modal/skeleton';
import { ModalStep } from '@components/common/multiStepModal';
import { ContextMenuV2 } from '@components/common/contextMenu';
import { useMultiStepModalStore } from '@components/common/multiStepModal/modalStore';
import { canCreateAccount } from '@hooks/useActionGuard/utils/permissionChecks';
import { BaseText, LargeText } from '@ui-library/typography';
import { ApiAccount } from '@api';

import { useDesignVariant } from '../../designVariantContext';
import { AccountManagementStore, useAccountManagementStore } from '../../store';
import { useDeleteAccounts } from '../../hooks/dataUpdater';
import { useAccountResourceActions } from '../../resourceActionsContext';
import { useDeleteAccountPermissions } from './useDeleteAccountPermissions';
import { useEditAccountPermissions } from './useEditAccountPermissions';
import { AccountDetails } from '../accountDetails';
import { SelectableCard } from '../../styles';
import { CurrentAccountBadge } from '../currentAccountBadge';
import { getMetadataSearchString } from '../utils';

interface AccountPickerProps {
  initialAccountId?: string;
  selectedAccountId?: string;
}

type RowPermission = { isAllowed: boolean; denialReason?: React.ReactNode };

interface AccountPickerRowProps {
  store: AccountManagementStore;
  account: ApiAccount;
  isSelected: boolean;
  isActive: boolean;
  showDelete: boolean;
  isOAuth: boolean;
  editPermission: RowPermission;
  deletePermission: RowPermission;
  variant?: 'next';
  isNext: boolean;
  isConfirmingDelete: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * A single selectable account row: details, a Current badge on the active account,
 * and an Edit/Delete overflow menu. Delete is offered for every non-active account
 * and, for data-retention, on the active account (routed to Disconnect upstream).
 */
const AccountPickerRow: React.FC<AccountPickerRowProps> = observer(
  ({
    store,
    account,
    isSelected,
    isActive,
    showDelete,
    isOAuth,
    editPermission,
    deletePermission,
    variant,
    isNext,
    isConfirmingDelete,
    scrollContainerRef,
    onSelect,
    onEdit,
    onDelete,
  }) => {
    const menuItems = [
      ...(isOAuth
        ? []
        : [
            {
              key: 'edit-account',
              label: <Tooltip title={editPermission.denialReason}>Edit account</Tooltip>,
              icon: <FontAwesomeIcon icon={regular('pen-to-square')} />,
              onClick: onEdit,
              disabled: !editPermission.isAllowed,
            },
          ]),
      ...(showDelete
        ? [
            {
              key: 'delete-account',
              label: <Tooltip title={deletePermission.denialReason}>Delete account</Tooltip>,
              danger: true,
              icon: <FontAwesomeIcon icon={regular('trash')} />,
              onClick: onDelete,
              disabled: !deletePermission.isAllowed,
            },
          ]
        : []),
    ];

    return (
      <SelectableCard isSelected={isSelected} variant={variant} onClick={onSelect}>
        <Flex flexGrow flexDirection="column" justifyContent="space-between" gap="16px">
          <Flex flexDirection="row" justifyContent="space-between" alignItemsCenter>
            <AccountDetails store={store} accountId={account.id} />
            <Flex gap="8px" alignItemsCenter>
              {isActive && <CurrentAccountBadge />}
              {menuItems.length > 0 && (
                // The key remounts the menu the moment this row's delete confirmation
                // opens, which destroys the open dropdown synchronously instead of
                // letting it animate out. That matters only here: the dropdown popup is
                // portalled to <body> at z-index 1150, while the confirmation lives
                // inside the picker's own modal (.ant-modal-wrap, z-index 1000), so a
                // dropdown still playing its ~100ms `slide-up-leave` paints *over* the
                // confirmation — no z-index on the confirmation can win, since it is
                // trapped in the modal's stacking context. Source/destination deletes
                // never hit this: they navigate to another step, which unmounts the row
                // (and its menu) outright. Remounting rather than unmounting the menu
                // keeps the ⋯ button in place — it stays visible beside the narrower
                // confirmation card, so hiding it would be its own visible glitch.
                <ContextMenuV2
                  key={isConfirmingDelete ? 'confirming-delete' : 'idle'}
                  variant={variant}
                  scrollContainerRef={scrollContainerRef}
                  items={menuItems}
                  size={isNext ? 'middle' : 'small'}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </SelectableCard>
    );
  },
);

export const AccountPickerModalStep: React.FC<AccountPickerProps> = observer(
  ({ initialAccountId, selectedAccountId }) => {
    const accountManagementStore = useAccountManagementStore();
    const multiStepModalStore = useMultiStepModalStore();
    const {
      resourceType,
      compatibleAccounts,
      compatibleAccountDefinitions,
      resourceDefinitionName,
      setAccountId,
      setDeleteAccountId,
      setEditAccountId,
      isAccountOAuth,
      adapter,
    } = accountManagementStore;

    const { token } = useTheme();
    const resourceActions = useAccountResourceActions();
    const deleteAccounts = useDeleteAccounts();

    // Supplying this confirmation is what makes the *active* account deletable: with
    // no confirmation for the disconnect it implies, the picker offers delete only on
    // non-active accounts (the source/destination behaviour).
    const ActiveAccountDeleteConfirmation = adapter?.ActiveAccountDeleteConfirmation;

    const variant = useDesignVariant();
    const isNext = variant === 'next';

    const [newAccountId, setNewAccountId] = useState<string>(
      selectedAccountId ?? initialAccountId ?? '',
    );
    const [searchText, setSearchText] = useState<string>('');
    // Data-retention "Use account" runs a validated-select; a 400/404 keeps the
    // previously-active account and surfaces the reason inline.
    const [activateError, setActivateError] = useState<string | undefined>(undefined);
    const [activating, setActivating] = useState(false);
    // The active account is deleted through the resource's own confirmation.
    const [disconnectDeleteAccountId, setDisconnectDeleteAccountId] = useState<string | undefined>(
      undefined,
    );
    // Drives the picker's footer while the DELETE is in flight: the confirmation hides
    // itself the moment it is confirmed, so the picker is what the user is left looking
    // at. That self-hide is also what makes a second confirm impossible, so no
    // re-entrancy guard is needed here (see accountPickerDisconnectDelete.test.tsx).
    const [disconnectDeleting, setDisconnectDeleting] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const sortedCompatibleAccounts = React.useMemo(() => {
      const [current, others] = partition(compatibleAccounts, (a) => a.id === initialAccountId);
      const sortedOthers = orderBy(others, ['updatedAt'], ['desc']);
      return current.length > 0 ? [...current, ...sortedOthers] : sortedOthers;
    }, [compatibleAccounts, initialAccountId]);

    const filteredCompatibleAccounts = React.useMemo(() => {
      if (!searchText) {
        return sortedCompatibleAccounts;
      }

      const query = searchText.toLowerCase();
      return sortedCompatibleAccounts.filter((account) => {
        const accountDefinition = compatibleAccountDefinitions.find(
          (def) => def.name === account.accountDefinitionName,
        );

        const nameMatch = account.name.toLowerCase().includes(query);
        const authTypeMatch = accountDefinition?.authenticationType?.toLowerCase().includes(query);
        const metadataMatch = getMetadataSearchString(account.metadata)
          .toLowerCase()
          .includes(query);

        return nameMatch || authTypeMatch || metadataMatch;
      });
    }, [sortedCompatibleAccounts, searchText, compatibleAccountDefinitions]);

    const { deletePermissionsMap } = useDeleteAccountPermissions(
      filteredCompatibleAccounts,
      resourceType,
    );

    const { editPermissionsMap } = useEditAccountPermissions(
      filteredCompatibleAccounts,
      resourceType,
    );

    const handleDeleteClick = (accountId: string) => {
      // Deleting the account in use also disconnects the resource from it, so where a
      // resource supplies its own confirmation for that, route to it instead of the
      // plain delete confirm.
      if (ActiveAccountDeleteConfirmation && accountId === initialAccountId) {
        setDisconnectDeleteAccountId(accountId);
        return;
      }
      setDeleteAccountId(accountId);
      multiStepModalStore.navigate('deleteAccountConfirmation');
    };

    const handleEditClick = (accountId: string) => {
      setEditAccountId(accountId);
      multiStepModalStore.navigate('editAccountForm');
    };

    const handleUseAccount = async () => {
      // A host that has to persist the selection — and can refuse it — supplies
      // `activate`. Without one (source, destination) the selection is simply
      // recorded on the store.
      if (resourceActions) {
        setActivateError(undefined);
        setActivating(true);
        const result = await resourceActions.activate(newAccountId);
        setActivating(false);
        if (result.ok) {
          resourceActions.close();
        } else {
          setActivateError(result.error ?? 'Failed to switch storage account');
        }
        return;
      }
      setAccountId(newAccountId);
    };

    const handleDisconnectDelete = async (accountId: string) => {
      setDisconnectDeleting(true);

      const successful = await deleteAccounts(accountId, resourceDefinitionName);

      setDisconnectDeleting(false);

      if (successful) {
        setDisconnectDeleteAccountId(undefined);
        // The backend cleared the link to the deleted account; reflect the disconnect
        // and close.
        resourceActions?.onDisconnected();
        resourceActions?.close();
      }
    };

    const createAccountPermission = canCreateAccount(resourceType);

    const noSearchResults = searchText.trim() !== '' && filteredCompatibleAccounts.length === 0;

    return (
      <ModalStep
        gap="16px"
        disabled={newAccountId === '' || newAccountId === initialAccountId}
        header={{
          title: 'Choose from the list of existing accounts',
          description: 'Current denotes the account you are currently using',
        }}
        footer={{
          backButton: {
            visible: true,
            text: 'Add account',
            type: 'secondary',
            size: 'middle',
            icon: !isNext && <FontAwesomeIcon icon={regular('plus')} />,
            onClick: () => {
              // Entering the create flow must start from a blank form. The store's
              // antd form instance is shared across every step of this modal, so
              // values a prior edit wrote (its name + options, which antd preserves
              // across the edit step's unmount) would otherwise pre-fill the create
              // form. Reset clears them, along with the edit/provider/definition
              // cross-step selection, before the create sub-tree opens. Mirrors the
              // reset the host performs when it first opens the modal (see
              // `resetStoreAndShowModal` in accountManagement/index.tsx).
              accountManagementStore.reset();
              multiStepModalStore.navigate('accountTypePicker');
            },
            disabled: !createAccountPermission.isAllowed,
            tooltip: { title: createAccountPermission.denialReason },
          },
          actionButton: {
            visible: true,
            text: 'Use account',
            onClick: handleUseAccount,
          },
          // The Disconnect confirmation closes itself on confirm, so the picker is
          // what the user is looking at while the DELETE is in flight.
          loading: activating || disconnectDeleting,
        }}
      >
        <InputField
          variant={variant}
          placeholder="Search by name, metadata etc"
          value={searchText}
          prefix={<FontAwesomeIcon icon={regular('search')} size="sm" />}
          onChange={(e) => setSearchText(e.target.value)}
          className="m-b-md"
        />
        <Flex
          ref={scrollContainerRef}
          flexGrow
          flexDirection="column"
          gap=".5rem"
          style={{ overflowY: 'auto', height: '50vh', padding: '2px' }}
        >
          {noSearchResults ? (
            <Flex flexDirection="column" flexGrow centered>
              <Svg name="empty_box" size={200} />
              <LargeText>No accounts found</LargeText>
            </Flex>
          ) : (
            filteredCompatibleAccounts.map((account) => (
              <AccountPickerRow
                key={account.id}
                store={accountManagementStore}
                account={account}
                isSelected={newAccountId === account.id}
                isActive={initialAccountId === account.id}
                showDelete={initialAccountId !== account.id || !!ActiveAccountDeleteConfirmation}
                isOAuth={isAccountOAuth(account.id)}
                editPermission={editPermissionsMap[account.id]}
                deletePermission={deletePermissionsMap[account.id]}
                variant={variant}
                isNext={isNext}
                // Only ever true where the resource supplies an active-account delete
                // confirmation: `disconnectDeleteAccountId` is set exclusively by that
                // path.
                isConfirmingDelete={disconnectDeleteAccountId === account.id}
                scrollContainerRef={scrollContainerRef}
                onSelect={() => setNewAccountId(account.id)}
                onEdit={() => handleEditClick(account.id)}
                onDelete={() => handleDeleteClick(account.id)}
              />
            ))
          )}
        </Flex>
        {activateError && (
          <BaseText color={token.colorErrorText} data-testid="dr-activate-error">
            {activateError}
          </BaseText>
        )}
        {ActiveAccountDeleteConfirmation && disconnectDeleteAccountId && (
          // Render the confirmation through the shared modal backdrop so it mounts as
          // a centered overlay on top of the picker (the same portal-less custom modal
          // card is centered by ModalOverlay: position fixed, full viewport, mask).
          // Without this it renders inline at the bottom of the picker's scroll
          // content instead of as a pop-up.
          <Backdrop show modalClosed={() => setDisconnectDeleteAccountId(undefined)}>
            <ActiveAccountDeleteConfirmation
              onConfirm={() => handleDisconnectDelete(disconnectDeleteAccountId)}
              hideModal={() => setDisconnectDeleteAccountId(undefined)}
            />
          </Backdrop>
        )}
      </ModalStep>
    );
  },
);
